import { useLazyQuery } from '@apollo/client'
import { notify } from 'components/toastify'
import { MESSAGES, STATUS } from 'constants/messages'
import { useSession } from 'helpers/checkSession'
import {
  GET_BOND_FILE_DETAIL,
  GET_DETAIL_ROW_BOND_DATA_UPLOAD_BY_FILE_ID,
  GET_FILE_BY_PAGINATION
} from 'queries/adminPortal'
import React, { useEffect, useState } from 'react'
import { IBondFileDetail, IFile, RowBondDataDetail } from '../interface'
import DetailBondFile from './components/detail-bond-file'
import DropdownSearchInput, { IDropdownOption } from './components/dropdown-search-input'

interface Props {
  dataInput: any
}

const DetailFile: React.FC<Props> = ({ dataInput }) => {
  const [dropdownOptions, setDropdownOptions] = useState<IDropdownOption[]>([
    { label: '', value: '', fileId: 0 },
  ])
  const [defaultDropdownOption, setDefaultDropdownOption] = useState<IDropdownOption>({
    label: dataInput?.fileName ?? '',
    value: dataInput?.bucketFileName ?? '',
    fileId: dataInput?.fileId ?? 0,
  })

  const [fileOverview, setFileOverview] = useState<IFile>({
    fileId: 0,
    fileName: '',
    bucketFileName: '',
    status: '',
    totalRow: 0,
    totalFailure: 0,
    totalSuccess: 0,
    createdAt: '',
    updatedAt: '',
    createdBy: '',
  })

  const [bondData, setBondData] = useState<RowBondDataDetail[]>([])
  const { timeOutSession } = useSession()
  const [getFileByIdQuery] = useLazyQuery(GET_BOND_FILE_DETAIL)
  const [getDetailRowBondDataUploadByFileIdQuery] = useLazyQuery(GET_DETAIL_ROW_BOND_DATA_UPLOAD_BY_FILE_ID)
  const [getFilesQuery] = useLazyQuery(GET_FILE_BY_PAGINATION)

  const getFileData = async () => {
    try {
      const response = await getFilesQuery()
      if (response.error) {
        if (response.error.message === MESSAGES.ERROR.E_SESSION_TIME_OUT) {
          notify(MESSAGES.WARNING.W_TIMEOUT_SESSION, STATUS.WARNING)
          timeOutSession()
        } else {
          throw response.error
        }
      }
      const fileData = response.data.getFileByPagination

      if (fileData && Array.isArray(fileData)) {
        const options: IDropdownOption[] = fileData.map((file: IFile) => {
          return {
            label: file.fileName,
            value: file.bucketFileName,
            fileId: file.fileId,
          }
        })

        setDropdownOptions(options)
      }
    } catch (error) {
      notify(MESSAGES.ERROR.E_SOMETHING_WENT_WRONG, STATUS.ERROR)
    }
  }

  const handleGetRowBondDataUploadFileByFileId = async (fileId: number, status?: string) => {
    try {
      const response = await getDetailRowBondDataUploadByFileIdQuery({
        variables: { fileId, status },
        fetchPolicy: 'no-cache',
      })

      if (response.error) {
        if (response.error.message === MESSAGES.ERROR.E_SESSION_TIME_OUT) {
          notify(MESSAGES.WARNING.W_TIMEOUT_SESSION, STATUS.WARNING)
          timeOutSession()
        } else {
          throw response.error
        }
      }

      const data: RowBondDataDetail[] = response.data.getBondDataUploadFileByFileId

      if (data) {
        setBondData(data)
      }
    } catch (error) {
      notify(MESSAGES.ERROR.E_SOMETHING_WENT_WRONG, STATUS.ERROR)
    }
  }

  const handleGetDetailBondFile = async (fileId: number, status?: string) => {
    try {
      if (fileId) {
        const response = await getFileByIdQuery({ variables: { fileId }, fetchPolicy: 'no-cache' })

        if (response.error) {
          if (response.error.message === MESSAGES.ERROR.E_SESSION_TIME_OUT) {
            notify(MESSAGES.WARNING.W_TIMEOUT_SESSION, STATUS.WARNING)
            timeOutSession()
          } else {
            throw response.error
          }
        }

        if (response?.data?.getDetailBondFileByFileId) {
          const data: IBondFileDetail = response.data.getDetailBondFileByFileId
          const temp: IFile = {
            fileId: data.fileId,
            fileName: data.fileName,
            bucketFileName: data.bucketFileName,
            status: data.status,
            totalRow: data.totalRow,
            totalFailure: data.totalFailure,
            totalSuccess: data.totalSuccess,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            createdBy: data.createdBy,
          }

          setFileOverview(temp)
          await handleGetRowBondDataUploadFileByFileId(fileId, status)
        }
      }
    } catch (error) {
      notify(MESSAGES.ERROR.E_SOMETHING_WENT_WRONG, STATUS.ERROR)
    }
  }

  useEffect(() => {
    getFileData()
  }, [])

  useEffect(() => {
    if (dataInput && dataInput.fileId) {
      handleGetDetailBondFile(dataInput.fileId)
      const dropdownOption = dropdownOptions.find((item) => item.fileId === dataInput.fileId)
      handleGetRowBondDataUploadFileByFileId(dataInput.fileId, dataInput.status)
      if (dropdownOption) {
        setDefaultDropdownOption(dropdownOption)
      }
    }
  }, [dataInput])

  return (
    <div className="px-20 py-10 md:px-10">
      <div className="w-[30%]">
        Select file
        <DropdownSearchInput
          dropdownOptions={dropdownOptions}
          onSelectOption={handleGetDetailBondFile}
          defaultDropdownOption={defaultDropdownOption}
        />
      </div>
      Detail Info
      <DetailBondFile
        fileOverview={fileOverview}
        bondData={bondData}
        fetchMoreData={getDetailRowBondDataUploadByFileIdQuery}
      />
      <hr className="my-10 border-t-neutral-6" />
    </div>
  )
}

export default DetailFile
